import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip,
    CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, InputAdornment, Grid, Avatar, Stack, Card, CardContent,
    ToggleButton, ToggleButtonGroup, Zoom, Autocomplete,
    FormControl, Select, MenuItem, Fade
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../../config';
import Loading from '../../components/Loading';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import MapIcon from '@mui/icons-material/Map';
import GridOnIcon from '@mui/icons-material/GridOn';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import FolderSpecialRoundedIcon from '@mui/icons-material/FolderSpecialRounded';
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';

function generateGuid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

// Colorful Categories
const CATEGORIES = [
    { key: 'pdf', label: 'PDF Docs', accept: '.pdf', icon: <PictureAsPdfIcon />, color: '#FF4757', bg: '#FFE9EB' },
    { key: 'word', label: 'Word Docs', accept: '.doc,.docx', icon: <DescriptionIcon />, color: '#2a5298', bg: '#e8eef7' },
    { key: 'map_pdf', label: 'Map PDFs', accept: '.pdf', icon: <MapIcon />, color: '#2ED573', bg: '#E8FAEE' },
    { key: 'excel', label: 'Excel Sheets', accept: '.xls,.xlsx,.xlsm,.csv', icon: <GridOnIcon />, color: '#FFA502', bg: '#FFF5E5' },
];

const FIELD_MAP = { pdf: 'pdf_files', word: 'word_files', map_pdf: 'map_pdf_files', excel: 'excel_files' };

const TYPE_CHIPS = [
    { key: 'pdf', label: 'PDF', color: '#FF4757', bg: '#FFE9EB', icon: <PictureAsPdfIcon sx={{ fontSize: 14 }} /> },
    { key: 'word', label: 'Word', color: '#2a5298', bg: '#e8eef7', icon: <DescriptionIcon sx={{ fontSize: 14 }} /> },
    { key: 'map_pdf', label: 'Map', color: '#2ED573', bg: '#E8FAEE', icon: <MapIcon sx={{ fontSize: 14 }} /> },
    { key: 'excel', label: 'Excel', color: '#FFA502', bg: '#FFF5E5', icon: <GridOnIcon sx={{ fontSize: 14 }} /> },
];

const WORK_STATUSES = ['New', 'In Progress', 'Completed', 'Cancelled'];

const STATUS_STYLE = {
    'New': { color: '#2a5298', bg: '#e8eef7' },
    'In Progress': { color: '#FFA502', bg: '#FFF5E5' },
    'Completed': { color: '#2ED573', bg: '#E8FAEE' },
    'Cancelled': { color: '#FF4757', bg: '#FFE9EB' },
};

function Tendering() {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [openUpload, setOpenUpload] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadPhase, setUploadPhase] = useState('');
    const [name, setName] = useState('');
    const [countries, setCountries] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [countryId, setCountryId] = useState('');
    const [divisionId, setDivisionId] = useState('');
    const [filterCountry, setFilterCountry] = useState([]);
    const [filterDivision, setFilterDivision] = useState([]);
    const [filterStatus, setFilterStatus] = useState('All');
    const [files, setFiles] = useState({ pdf: [], word: [], map_pdf: [], excel: [] });
    const [filesDialogOpen, setFilesDialogOpen] = useState(false);
    const [filesSession, setFilesSession] = useState(null);
    const [filesList, setFilesList] = useState([]);
    const [filesLoading, setFilesLoading] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renameTarget, setRenameTarget] = useState(null);
    const [renameName, setRenameName] = useState('');
    const [renaming, setRenaming] = useState(false);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [dragOverCat, setDragOverCat] = useState(null);

    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    const fetchedRef = useRef(false);

    const fetchSessions = async () => {
        if (fetchedRef.current) return;
        fetchedRef.current = true;
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/tendering/list_sessions`, { headers });
            const body = res.data || {};
            const rawSessions = Array.isArray(body.sessions) ? body.sessions : (Array.isArray(body) ? body : []);

            const normalized = rawSessions.map((s, index) => {
                const guid = s?.session_guid || s?.session_id || s?.guid || s?.id || s?.key || null;
                const name = s?.name || s?.session_name || s?.title || guid || `Session ${index + 1}`;
                return {
                    ...s,
                    id: s?.id ?? guid ?? `tender-${index}`,
                    session_guid: guid,
                    name,
                    work_status: s?.work_status || s?.status || 'New',
                    file_count: s?.file_count ?? (s?.files ? s.files.length : 0),
                    file_types: s?.file_types || {},
                    created_at: s?.created_at || s?.createdAt || null,
                    created_by: s?.created_by || s?.createdBy || s?.created_by_name || '—',
                    country: s?.country || s?.country_name || '—',
                    division: s?.division || s?.division_name || '—'
                };
            });

            setSessions(normalized);
        } catch (err) {
            console.error('Error fetching tendering sessions:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchSessions(); }, []);

    useEffect(() => {
        const fetchSetup = async () => {
            try {
                const [cRes, dRes] = await Promise.all([
                    axios.get(`${API_URL}/setup/countries`, { headers }),
                    axios.get(`${API_URL}/setup/divisions`, { headers })
                ]);
                setCountries(cRes.data || []);
                setDivisions(dRes.data || []);
            } catch (err) {
                console.error('Error fetching countries/divisions:', err);
            }
        };
        fetchSetup();
    }, []);

    const resetUpload = () => {
        setName('');
        setCountryId('');
        setDivisionId('');
        setFiles({ pdf: [], word: [], map_pdf: [], excel: [] });
    };

    const handleSelectFiles = (category, fileList) => {
        const incoming = Array.from(fileList);
        setFiles(prev => {
            const merged = [...prev[category]];
            incoming.forEach(f => {
                if (!merged.some(x => x.name === f.name && x.size === f.size)) {
                    merged.push({ id: Math.random().toString(36).slice(2), name: f.name, size: f.size, file: f });
                }
            });
            return { ...prev, [category]: merged };
        });
    };

    const removeFile = (category, id) => {
        setFiles(prev => ({ ...prev, [category]: prev[category].filter(f => f.id !== id) }));
    };

    const acceptsFile = (category, fileName) => {
        const cat = CATEGORIES.find(c => c.key === category);
        if (!cat) return false;
        const exts = cat.accept.split(',').map(s => s.trim().toLowerCase());
        const ext = ('.' + (fileName || '').split('.').pop()).toLowerCase();
        return exts.includes(ext);
    };

    const handleDragOver = (e, category) => {
        e.preventDefault();
        e.stopPropagation();
        if (dragOverCat !== category) setDragOverCat(category);
    };

    const handleDragLeave = (e, category) => {
        e.preventDefault();
        e.stopPropagation();
        if (dragOverCat === category) setDragOverCat(null);
    };

    const handleDrop = (e, category) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverCat(null);
        const dropped = Array.from(e.dataTransfer?.files || []);
        if (dropped.length === 0) return;
        const valid = dropped.filter(f => acceptsFile(category, f.name));
        const invalid = dropped.filter(f => !acceptsFile(category, f.name));
        if (valid.length > 0) handleSelectFiles(category, valid);
        if (invalid.length > 0) {
            const cat = CATEGORIES.find(c => c.key === category);
            alert(`These file(s) are not allowed in "${cat?.label}" (accepts ${cat?.accept}):\n` +
                  invalid.map(f => `• ${f.name}`).join('\n'));
        }
    };

    const totalFileCount = Object.values(files).reduce((a, list) => a + list.length, 0);

    const handleUpload = async () => {
        if (totalFileCount === 0) { alert('Please select at least one file to upload.'); return; }
        if (!countryId || !divisionId) { alert('Please select the Country and Division.'); return; }
        const guid = generateGuid();
        const formData = new FormData();
        formData.append('session_id', guid);
        if (name.trim()) formData.append('name', name.trim());
        formData.append('country_id', countryId);
        formData.append('division_id', divisionId);
        Object.entries(files).forEach(([cat, list]) => {
            list.forEach(f => formData.append(FIELD_MAP[cat], f.file));
        });
        setUploading(true);
        setUploadPhase('Uploading files...');
        try {
            const res = await axios.post(`${API_URL}/tendering/upload`, formData, {
                headers: { ...headers, 'Content-Type': 'multipart/form-data' },
                timeout: 600000
            });
            const sessionId = res.data?.session_id || guid;
            setUploadPhase('Processing documents with AI...');
            await waitForProcessing(sessionId);
            navigate(`/tendering/${sessionId}/chat`, { state: { sessionName: name.trim() || 'Untitled Tender' } });
        } catch (err) {
            console.error('Upload error:', err);
            let msg = err.response?.data?.error || err.response?.data?.message;
            if (!msg) {
                if (err.code === 'ECONNABORTED') msg = 'The request timed out. Please try again or upload fewer/smaller files.';
                else if (err.message === 'Network Error') msg = 'Could not reach the server. Please check your connection and that the API is running.';
                else msg = err.message;
            }
            alert('Upload failed: ' + msg);
        } finally {
            setUploading(false);
            setUploadPhase('');
        }
    };

    const waitForProcessing = async (sessionGuid, maxAttempts = 300, intervalMs = 2000) => {
        for (let i = 0; i < maxAttempts; i++) {
            try {
                const res = await axios.get(`${API_URL}/tendering/sessions/${sessionGuid}`, { headers });
                const st = res.data?.status;
                if (st === 'Ready' || st === 'Failed') return st;
            } catch (err) {
                console.warn('Status poll failed, retrying...', err.message);
            }
            await new Promise(r => setTimeout(r, intervalMs));
        }
        return 'Timeout';
    };

    const fileTypeMeta = (ftype) => TYPE_CHIPS.find(c => c.key === ftype) || { label: 'File', color: '#636E72', bg: '#DFE6E9', icon: <DescriptionIcon sx={{ fontSize: 14 }} /> };

    const closePreview = () => {
        setPreviewFile(null);
        if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    };

    const openFilesDialog = async (sessionGuid) => {
        setFilesDialogOpen(true);
        setFilesLoading(true);
        setFilesList([]);
        setFilesSession(sessions.find(s => s.session_guid === sessionGuid) || null);
        try {
            const res = await axios.get(`${API_URL}/tendering/sessions/${sessionGuid}`, { headers });
            setFilesSession(res.data);
            setFilesList(res.data?.files || []);
        } catch (err) {
            console.error('Error fetching session files:', err);
            setFilesList([]);
        } finally {
            setFilesLoading(false);
        }
    };

    const closeFilesDialog = () => {
        setFilesDialogOpen(false);
        setFilesList([]);
        setFilesSession(null);
        closePreview();
    };

    const fetchFileBlob = async (file) => {
        const res = await axios.get(`${API_URL}/tendering/sessions/${filesSession.session_guid}/files/${file.id}/download`, {
            headers,
            responseType: 'blob'
        });
        return res.data;
    };

    const handlePreview = async (file) => {
        if (file.file_type === 'word' || file.file_type === 'excel') {
            alert('Preview is available for PDF files only. Please use Download for Word/Excel files.');
            return;
        }
        try {
            const blob = await fetchFileBlob(file);
            const url = URL.createObjectURL(blob);
            setPreviewUrl(url);
            setPreviewFile(file);
        } catch (err) {
            alert('Could not load preview: ' + (err.message || err));
        }
    };

    const handleDownload = async (file) => {
        try {
            const blob = await fetchFileBlob(file);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.file_name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (err) {
            alert('Download failed: ' + (err.message || err));
        }
    };

    const filteredSessions = sessions.filter(s => {
        const q = search.toLowerCase();
        const matchesSearch = (s.name || '').toLowerCase().includes(q) ||
               (s.session_guid || '').toLowerCase().includes(q) ||
               (s.created_by || '').toLowerCase().includes(q);
        const matchesCountry = filterCountry.length === 0 || filterCountry.includes(s.country_id);
        const matchesDivision = filterDivision.length === 0 || filterDivision.includes(s.division_id);
        const matchesStatus = filterStatus === 'All' || (s.work_status || 'New') === filterStatus;
        return matchesSearch && matchesCountry && matchesDivision && matchesStatus;
    });

    const handleWorkStatusChange = async (sessionGuid, newStatus) => {
        try {
            await axios.put(`${API_URL}/tendering/sessions/${sessionGuid}/work-status`,
                { work_status: newStatus },
                { headers: { ...headers, 'Content-Type': 'application/json' } }
            );
            setSessions(prev => prev.map(s =>
                s.session_guid === sessionGuid ? { ...s, work_status: newStatus } : s
            ));
        } catch (err) {
            console.error('Update status error:', err);
            alert('Could not update status: ' + (err.response?.data?.error || err.message));
        }
    };

    const openRenameDialog = (session) => {
        setRenameTarget(session);
        setRenameName(session?.name || '');
        setRenameDialogOpen(true);
    };

    const closeRenameDialog = () => {
        setRenameDialogOpen(false);
        setRenameTarget(null);
        setRenameName('');
        setRenaming(false);
    };

    const handleRename = async () => {
        const name = (renameName || '').trim();
        if (!name || !renameTarget) return;
        setRenaming(true);
        try {
            const res = await axios.post(`${API_URL}/tendering/rename_session`,
                { session_id: renameTarget.session_guid, session_name: name },
                { headers: { ...headers, 'Content-Type': 'application/json' } }
            );
            const newName = res.data?.session_name || name;
            setSessions(prev => prev.map(s =>
                s.session_guid === renameTarget.session_guid ? { ...s, name: newName } : s
            ));
            closeRenameDialog();
        } catch (err) {
            console.error('Rename error:', err);
            alert('Rename failed: ' + (err.response?.data?.message || err.response?.data?.error || err.message));
            setRenaming(false);
        }
    };

    const openDeleteDialog = (session) => {
        setDeleteTarget(session);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setDeleteTarget(null);
        setDeleting(false);
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await axios.post(`${API_URL}/tendering/delete_session`,
                { session_id: deleteTarget.session_guid },
                { headers: { ...headers, 'Content-Type': 'application/json' } }
            );
            setSessions(prev => prev.filter(s => s.session_guid !== deleteTarget.session_guid));
            closeDeleteDialog();
        } catch (err) {
            console.error('Delete error:', err);
            alert('Delete failed: ' + (err.response?.data?.message || err.response?.data?.error || err.message));
            setDeleting(false);
        }
    };

    const typeChipsFor = (ft) => TYPE_CHIPS.filter(c => ft && ft[c.key]).map(c => ({ ...c, count: ft[c.key] }));

    if (loading) return <Loading />;

    // Custom Input Style
    const friendlyInputStyle = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '16px',
            bgcolor: '#FFFFFF',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 14px rgba(0,0,0,0.02)',
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': { borderColor: '#A5B1C2' },
            '&.Mui-focused fieldset': { borderColor: '#1e3c72', borderWidth: '2px' },
            '&.Mui-focused': { boxShadow: '0 4px 20px rgba(37, 99, 235, 0.1)' }
        }
    };

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box', bgcolor: '#F8F9FE', minHeight: 'calc(100vh - 4rem)', pb: 0, fontFamily: '"Nunito", "Segoe UI", sans-serif' }}>
            
            {/* Colorful & Friendly Header Banner */}
            <Box sx={{ 
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
                pt: { xs: 4, md: 6 }, pb: { xs: 8, md: 10 }, px: { xs: 3, md: 5 },
                borderRadius: '0 0 40px 40px', mb: -4,
                boxShadow: '0 15px 35px rgba(30, 60, 114, 0.2)'
            }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 3 }}>
                    <Box>
                        <Typography variant="h3" fontWeight="800" sx={{ color: '#FFFFFF', mb: 1, textShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                            Tendering Pro
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                            Upload, analyze, and manage your tenders smartly.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddCircleRoundedIcon />}
                        onClick={() => { resetUpload(); setOpenUpload(true); }}
                        sx={{
                            borderRadius: '50px', fontWeight: 800, px: 4, py: 1.5, fontSize: '1rem', textTransform: 'none',
                            bgcolor: '#FFFFFF', color: '#1e3c72',
                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                            '&:hover': { bgcolor: '#F1F2F6', transform: 'translateY(-2px)', boxShadow: '0 12px 30px rgba(0,0,0,0.2)' },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        Create Tender
                    </Button>
                </Box>
            </Box>

            {/* Main Content */}
            <Box sx={{ px: { xs: 3, md: 5 }, position: 'relative', zIndex: 2 }}>
                
                {/* Floating Filter Bar */}
                <Paper sx={{ 
                    p: 2, display: 'flex', flexDirection: { xs: 'column', xl: 'row' }, gap: 2, 
                    alignItems: { xs: 'stretch', xl: 'center' }, justifyContent: 'space-between',
                    borderRadius: '24px', mb: 5, boxShadow: '0 10px 40px rgba(0,0,0,0.05)', border: '1px solid #F1F2F6'
                }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', p: 0.5 }}>
                        {['All', ...WORK_STATUSES].map(f => (
                            <Chip
                                key={f} label={f} onClick={() => setFilterStatus(f)}
                                sx={{
                                    borderRadius: '50px', fontWeight: 700, fontSize: '0.85rem', px: 1.5, py: 2.5,
                                    bgcolor: filterStatus === f ? '#1e3c72' : '#F1F2F6',
                                    color: filterStatus === f ? '#FFFFFF' : '#747D8C',
                                    transition: 'all 0.3s ease', cursor: 'pointer',
                                    '&:hover': { bgcolor: filterStatus === f ? '#1e3c72' : '#ECCC68', color: filterStatus === f ? '#FFFFFF' : '#2F3542' }
                                }}
                            />
                        ))}
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' }, flexGrow: 1, justifyContent: 'flex-end' }}>
                        <TextField
                            placeholder="Search magic..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#A5B1C2' }} /></InputAdornment> }}
                            sx={{ minWidth: 220, ...friendlyInputStyle }}
                        />
                        <FormControl sx={{ minWidth: 150, ...friendlyInputStyle }}>
                            <Select 
                                displayEmpty multiple value={filterCountry} onChange={e => setFilterCountry(e.target.value)}
                                renderValue={(selected) => selected.length === 0 ? <span style={{color:'#A5B1C2'}}>Country</span> : selected.map(id => countries.find(c => c.id === id)?.name).join(', ')}
                            >
                                {countries.map(c => <MenuItem key={c.id} value={c.id} sx={{ fontWeight: 600, borderRadius: '8px', m: 0.5 }}>{c.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 150, ...friendlyInputStyle }}>
                            <Select 
                                displayEmpty multiple value={filterDivision} onChange={e => setFilterDivision(e.target.value)}
                                renderValue={(selected) => selected.length === 0 ? <span style={{color:'#A5B1C2'}}>Division</span> : selected.map(id => divisions.find(d => d.id === id)?.name).join(', ')}
                            >
                                {divisions.map(d => <MenuItem key={d.id} value={d.id} sx={{ fontWeight: 600, borderRadius: '8px', m: 0.5 }}>{d.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        
                        <ToggleButtonGroup
                            value={viewMode} exclusive onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
                            sx={{ 
                                bgcolor: '#FFFFFF', borderRadius: '16px', p: 0.5, boxShadow: '0 4px 14px rgba(0,0,0,0.02)',
                                '& .MuiToggleButton-root': { border: 'none', color: '#A5B1C2', borderRadius: '12px !important', mx: 0.5 },
                                '& .Mui-selected': { bgcolor: '#e8eef7 !important', color: '#1e3c72 !important', boxShadow: '0 2px 8px rgba(30,60,114,0.1)' }
                            }}
                        >
                            <ToggleButton value="list"><ViewListRoundedIcon /></ToggleButton>
                            <ToggleButton value="grid"><GridViewRoundedIcon /></ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Paper>

                <Zoom in={true} timeout={500}>
                    <Box>
                        {viewMode === 'grid' ? (
                            <Grid container spacing={3}>
                                {filteredSessions.map((s) => {
                                    const st = STATUS_STYLE[s.work_status || 'New'];
                                    return (
                                        <Grid item xs={12} sm={6} md={4} lg={3} key={s.id}>
                                            <Card 
                                                elevation={0}
                                                onClick={() => navigate(`/tendering/${s.session_guid}/chat`, { state: { sessionName: s.name || 'Untitled Tender' } })}
                                                sx={{ 
                                                    height: '100%', display: 'flex', flexDirection: 'column',
                                                    borderRadius: '24px', bgcolor: '#FFFFFF', cursor: 'pointer',
                                                    border: '1px solid #F1F2F6',
                                                    boxShadow: '0 10px 30px rgba(0,0,0,0.03)',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    '&:hover': { 
                                                        transform: 'translateY(-8px)', 
                                                        boxShadow: `0 20px 40px rgba(30, 60, 114, 0.15)`,
                                                        borderColor: '#1e3c72'
                                                    }
                                                }} 
                                            >
                                                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                                                    {/* Header Card */}
                                                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                                        <Avatar sx={{ 
                                                            bgcolor: st.bg, color: st.color, 
                                                            width: 48, height: 48, fontWeight: 800, fontSize: '1.2rem',
                                                            borderRadius: '16px'
                                                        }}>
                                                            {(s.name || 'T').charAt(0).toUpperCase()}
                                                        </Avatar>
                                                        <Box sx={{ overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                            <Typography variant="h6" fontWeight="800" color="#2F3542" noWrap>{s.name || 'Untitled Tender'}</Typography>
                                                            <Typography variant="caption" color="#A5B1C2" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <AccessTimeFilledIcon sx={{ fontSize: 14 }} /> {s.created_at ? new Date(s.created_at).toLocaleDateString() : 'New'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    {/* Tags */}
                                                    <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                        <Chip label={s.work_status || 'New'} sx={{ fontWeight: 800, fontSize: '0.7rem', borderRadius: '8px', bgcolor: st.bg, color: st.color, border: 'none', height: 26 }} />
                                                        {s.country && <Chip icon={<PublicRoundedIcon sx={{ fontSize: '14px !important', color: '#1e3c72' }} />} label={s.country} sx={{ fontWeight: 700, fontSize: '0.7rem', borderRadius: '8px', bgcolor: '#F1F2F6', color: '#57606F', height: 26 }} />}
                                                        {s.division && <Chip icon={<BusinessRoundedIcon sx={{ fontSize: '14px !important', color: '#FF4757' }} />} label={s.division} sx={{ fontWeight: 700, fontSize: '0.7rem', borderRadius: '8px', bgcolor: '#F1F2F6', color: '#57606F', height: 26 }} />}
                                                    </Box>

                                                    {/* Files Area */}
                                                    <Box sx={{ p: 2, bgcolor: '#F8F9FE', borderRadius: '16px' }}>
                                                        <Typography variant="caption" fontWeight="800" color="#A5B1C2" sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>Attached Files</Typography>
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                            {typeChipsFor(s.file_types).map((c) => (
                                                                <Tooltip title={`${c.count} ${c.label}`} key={c.key}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: c.bg, color: c.color, px: 1.2, py: 0.6, borderRadius: '10px' }}>
                                                                        {c.icon}
                                                                        <Typography variant="caption" sx={{ fontWeight: 800 }}>{c.count}</Typography>
                                                                    </Box>
                                                                </Tooltip>
                                                            ))}
                                                            {s.file_count === 0 && <Typography variant="caption" color="#A5B1C2" fontWeight={600}>No files uploaded yet.</Typography>}
                                                        </Stack>
                                                    </Box>
                                                </CardContent>
                                                
                                                {/* Actions Footer */}
                                                <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, bgcolor: '#FFFFFF', borderTop: '2px dashed #F1F2F6', borderRadius: '0 0 24px 24px' }}>
                                                    <Tooltip title="View Files">
                                                        <IconButton onClick={(e) => { e.stopPropagation(); openFilesDialog(s.session_guid); }} sx={{ color: '#1e3c72', bgcolor: '#e8eef7', '&:hover': { bgcolor: '#1e3c72', color: 'white' } }}><FolderSpecialRoundedIcon fontSize="small" /></IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Rename">
                                                        <IconButton onClick={(e) => { e.stopPropagation(); openRenameDialog(s); }} sx={{ color: '#FFA502', bgcolor: '#FFF5E5', '&:hover': { bgcolor: '#FFA502', color: 'white' } }}><EditRoundedIcon fontSize="small" /></IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Chat & Details">
                                                        <IconButton onClick={(e) => { e.stopPropagation(); navigate(`/tendering/${s.session_guid}/chat`, { state: { sessionName: s.name || 'Untitled Tender' } }); }} sx={{ color: '#1e3c72', bgcolor: '#e8eef7', '&:hover': { bgcolor: '#1e3c72', color: 'white' } }}><ChatBubbleRoundedIcon fontSize="small" /></IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton onClick={(e) => { e.stopPropagation(); openDeleteDialog(s); }} sx={{ color: '#FF4757', bgcolor: '#FFE9EB', '&:hover': { bgcolor: '#FF4757', color: 'white' } }}><DeleteIcon fontSize="small" /></IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        ) : (
                            <Paper elevation={0} sx={{ borderRadius: '24px', overflow: 'hidden', bgcolor: 'white', boxShadow: '0 10px 40px rgba(0,0,0,0.04)' }}>
                                <TableContainer>
                                    <Table sx={{ minWidth: 900, '& .MuiTableCell-root': { borderColor: '#F1F2F6', py: 2, px: 3 } }}>
                                        <TableHead sx={{ bgcolor: '#F8F9FE' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#A5B1C2', textTransform: 'uppercase' }}>Tender Info</TableCell>
                                                <TableCell sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#A5B1C2', textTransform: 'uppercase' }}>Location</TableCell>
                                                <TableCell sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#A5B1C2', textTransform: 'uppercase' }}>Progress</TableCell>
                                                <TableCell sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#A5B1C2', textTransform: 'uppercase' }}>Files</TableCell>
                                                <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#A5B1C2', textTransform: 'uppercase' }}>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredSessions.map((s) => {
                                                const st = STATUS_STYLE[s.work_status || 'New'];
                                                return (
                                                    <TableRow key={s.id} hover sx={{ transition: 'all 0.2s', '&:hover': { bgcolor: '#F8F9FE' } }}>
                                                        <TableCell sx={{ cursor: 'pointer' }} onClick={() => navigate(`/tendering/${s.session_guid}/chat`, { state: { sessionName: s.name || 'Untitled Tender' } })}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                <Avatar sx={{ bgcolor: st.bg, color: st.color, borderRadius: '12px', fontWeight: 800 }}>{(s.name || 'T').charAt(0).toUpperCase()}</Avatar>
                                                                <Box>
                                                                    <Typography variant="body1" fontWeight="800" color="#2F3542">{s.name || 'Untitled Tender'}</Typography>
                                                                    <Typography variant="caption" color="#A5B1C2" fontWeight="600">{s.session_guid.substring(0,8)}</Typography>
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight="800" color="#57606F" display="flex" alignItems="center" gap={0.5}><PublicRoundedIcon fontSize="small" sx={{color:'#1e3c72'}}/> {s.country || '—'}</Typography>
                                                            <Typography variant="caption" fontWeight="700" color="#A5B1C2" display="flex" alignItems="center" gap={0.5} mt={0.5}><BusinessRoundedIcon fontSize="small" sx={{color:'#FF4757'}}/> {s.division || '—'}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Select
                                                                size="small" value={s.work_status || 'New'} onChange={(e) => handleWorkStatusChange(s.session_guid, e.target.value)}
                                                                sx={{
                                                                    fontSize: '0.85rem', fontWeight: 800, borderRadius: '12px', height: 36, color: st.color, bgcolor: st.bg,
                                                                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                                                    boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
                                                                }}
                                                            >
                                                                {WORK_STATUSES.map(stOpt => <MenuItem key={stOpt} value={stOpt} sx={{ fontWeight: 700, borderRadius: '8px', m: 0.5 }}>{stOpt}</MenuItem>)}
                                                            </Select>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                                {typeChipsFor(s.file_types).map((c) => (
                                                                    <Box key={c.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: c.bg, color: c.color, px: 1, py: 0.5, borderRadius: '8px' }}>
                                                                        {React.cloneElement(c.icon, { sx: { fontSize: 16 } })}
                                                                        <Typography variant="caption" sx={{ fontWeight: 800 }}>{c.count}</Typography>
                                                                    </Box>
                                                                ))}
                                                                {s.file_count === 0 && <Typography variant="caption" color="#A5B1C2" fontWeight={700}>—</Typography>}
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                                <IconButton size="small" onClick={() => openFilesDialog(s.session_guid)} sx={{ color: '#1e3c72', bgcolor: '#e8eef7', '&:hover': { bgcolor: '#1e3c72', color: 'white' } }}><FolderSpecialRoundedIcon fontSize="small" /></IconButton>
                                                                <IconButton size="small" onClick={() => openRenameDialog(s)} sx={{ color: '#FFA502', bgcolor: '#FFF5E5', '&:hover': { bgcolor: '#FFA502', color: 'white' } }}><EditRoundedIcon fontSize="small" /></IconButton>
                                                                <IconButton size="small" onClick={() => openDeleteDialog(s)} sx={{ color: '#FF4757', bgcolor: '#FFE9EB', '&:hover': { bgcolor: '#FF4757', color: 'white' } }}><DeleteIcon fontSize="small" /></IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                            {filteredSessions.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.5 }}>
                                                            <FolderSpecialRoundedIcon sx={{ fontSize: 60, color: '#A5B1C2', mb: 2 }} />
                                                            <Typography variant="h6" fontWeight="800" color="#2F3542">Nothing here yet!</Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}
                    </Box>
                </Zoom>
            </Box>

            {/* Custom Dialog Styles to make them very modern and friendly */}
            <style>{`
                .MuiDialog-paper { border-radius: 24px !important; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important; overflow: visible !important; }
                .MuiBackdrop-root { background-color: rgba(47, 53, 66, 0.6) !important; backdrop-filter: blur(5px); }
            `}</style>

            {/* Files Dialog */}
            <Dialog open={filesDialogOpen} onClose={closeFilesDialog} maxWidth="sm" fullWidth TransitionComponent={Fade}>
                <Box sx={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', p: 3, borderRadius: '24px 24px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white' }}>
                    <Box>
                        <Typography variant="h5" fontWeight="800">Tender Files</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>{filesSession?.name || 'Tender'} — {filesList.length} files</Typography>
                    </Box>
                    <IconButton onClick={closeFilesDialog} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' } }}><CloseRoundedIcon /></IconButton>
                </Box>
                <DialogContent sx={{ p: 4, bgcolor: '#F8F9FE', borderRadius: '0 0 24px 24px' }}>
                    {filesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress sx={{ color: '#1e3c72' }} /></Box>
                    ) : filesList.length === 0 ? (
                        <Box sx={{ py: 5, textAlign: 'center' }}>
                            <Typography variant="h6" fontWeight="800" color="#A5B1C2">No files to show.</Typography>
                        </Box>
                    ) : (
                        <Stack spacing={2}>
                            {filesList.map((f) => {
                                const meta = fileTypeMeta(f.file_type);
                                return (
                                    <Box key={f.id} sx={{ p: 2, borderRadius: '16px', bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 4px 15px rgba(0,0,0,0.03)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: '12px', bgcolor: meta.bg, color: meta.color }}>
                                            {React.cloneElement(meta.icon, { sx: { fontSize: 24 } })}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="subtitle1" fontWeight="800" color="#2F3542" noWrap title={f.file_name}>{f.file_name}</Typography>
                                            <Typography variant="caption" fontWeight="700" color="#A5B1C2">{meta.label}</Typography>
                                        </Box>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton onClick={() => handlePreview(f)} sx={{ bgcolor: '#F1F2F6', color: '#57606F', '&:hover': { bgcolor: '#1e3c72', color: 'white' } }}><VisibilityRoundedIcon /></IconButton>
                                            <IconButton onClick={() => handleDownload(f)} sx={{ bgcolor: '#F1F2F6', color: '#57606F', '&:hover': { bgcolor: '#2ED573', color: 'white' } }}><DownloadRoundedIcon /></IconButton>
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog open={Boolean(previewFile)} onClose={closePreview} maxWidth="lg" fullWidth>
                <Box sx={{ background: '#2F3542', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white' }}>
                    <Typography variant="h6" fontWeight="800">{previewFile?.file_name || 'Preview'}</Typography>
                    <IconButton onClick={closePreview} sx={{ color: 'white' }}><CloseRoundedIcon /></IconButton>
                </Box>
                <DialogContent sx={{ p: 0, height: '85vh', bgcolor: '#F1F2F6' }}>
                    {previewUrl ? <iframe src={previewUrl} title="preview" style={{ width: '100%', height: '100%', border: 'none' }} /> : null}
                </DialogContent>
            </Dialog>

            {/* Upload Dialog */}
            <Dialog open={openUpload} onClose={() => { if (!uploading) setOpenUpload(false); }} maxWidth="md" fullWidth TransitionComponent={Fade}>
                <Box sx={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', p: 3, pb: 4, borderRadius: '24px 24px 0 0', color: 'white', position: 'relative' }}>
                    <Typography variant="h4" fontWeight="800" mb={1}>New Project Magic ✨</Typography>
                    <Typography variant="body1" fontWeight="600" sx={{ opacity: 0.9 }}>Let's setup your tender details and documents.</Typography>
                    {!uploading && (
                        <IconButton onClick={() => setOpenUpload(false)} sx={{ position: 'absolute', top: 20, right: 20, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' } }}>
                            <CloseRoundedIcon />
                        </IconButton>
                    )}
                </Box>
                
                <DialogContent sx={{ p: 4, pt: 5, bgcolor: '#F8F9FE', borderRadius: '0 0 24px 24px', mt: -2 }}>
                    <Box sx={{ mb: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField fullWidth label="Awesome Tender Name (Optional)" variant="outlined" value={name} onChange={(e) => setName(e.target.value)} sx={friendlyInputStyle} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={countries} getOptionLabel={(option) => option.name || ''} value={countries.find(c => c.id === countryId) || null} onChange={(e, v) => setCountryId(v ? v.id : '')}
                                    renderInput={(params) => <TextField {...params} label="Country *" required sx={friendlyInputStyle} />}
                                    componentsProps={{ paper: { sx: { borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' } } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={divisions} getOptionLabel={(option) => option.name || ''} value={divisions.find(d => d.id === divisionId) || null} onChange={(e, v) => setDivisionId(v ? v.id : '')}
                                    renderInput={(params) => <TextField {...params} label="Division *" required sx={friendlyInputStyle} />}
                                    componentsProps={{ paper: { sx: { borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' } } }}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#2F3542', mb: 3 }}>Drop Your Files Here 👇</Typography>
                    <Grid container spacing={3}>
                        {CATEGORIES.map((cat) => (
                            <Grid item xs={12} sm={6} key={cat.key}>
                                <Box
                                    onDragOver={(e) => handleDragOver(e, cat.key)} onDragEnter={(e) => handleDragOver(e, cat.key)} onDragLeave={(e) => handleDragLeave(e, cat.key)} onDrop={(e) => handleDrop(e, cat.key)}
                                    sx={{
                                        border: dragOverCat === cat.key ? `2px dashed ${cat.color}` : '2px dashed #DFE4EA', borderRadius: '24px', p: 3, minHeight: 140,
                                        bgcolor: dragOverCat === cat.key ? cat.bg : '#FFFFFF', transition: 'all 0.3s ease', display: 'flex', flexDirection: 'column',
                                        '&:hover': { borderColor: cat.color, transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(0,0,0,0.05)' }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{ p: 1, borderRadius: '12px', bgcolor: cat.bg, color: cat.color }}>{cat.icon}</Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#2F3542' }}>{cat.label}</Typography>
                                        </Box>
                                        <Button component="label" sx={{ borderRadius: '50px', textTransform: 'none', color: 'white', bgcolor: cat.color, fontWeight: 700, px: 2, py: 0.5, '&:hover': { bgcolor: cat.color, filter: 'brightness(0.9)' } }}>
                                            Add
                                            <input type="file" hidden multiple accept={cat.accept} onChange={(e) => { handleSelectFiles(cat.key, e.target.files); e.target.value = ''; }} />
                                        </Button>
                                    </Box>

                                    <Box sx={{ flexGrow: 1 }}>
                                        {files[cat.key].length === 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.6 }}>
                                                <CloudUploadRoundedIcon sx={{ fontSize: 32, color: cat.color, mb: 1 }} />
                                                <Typography variant="caption" fontWeight="700" color="#747D8C">Drag files here</Typography>
                                            </Box>
                                        ) : (
                                            <Stack spacing={1}>
                                                {files[cat.key].map((f) => (
                                                    <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#F8F9FE', border: '1px solid #DFE4EA', borderRadius: '12px', px: 1.5, py: 1 }}>
                                                        <Typography variant="caption" noWrap sx={{ maxWidth: '80%', fontWeight: 700, color: '#2F3542' }}>{f.name}</Typography>
                                                        <IconButton size="small" onClick={() => removeFile(cat.key, f.id)} sx={{ p: 0.5, color: '#FF4757', bgcolor: '#FFE9EB', '&:hover': { bgcolor: '#FF4757', color: 'white' } }}><CloseRoundedIcon sx={{ fontSize: 16 }} /></IconButton>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, px: 4, bgcolor: '#FFFFFF', borderRadius: '0 0 24px 24px', borderTop: '1px solid #F1F2F6' }}>
                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                        {uploading && <CircularProgress size={20} sx={{ color: '#1e3c72' }} />}
                        <Typography variant="subtitle2" fontWeight="800" color={uploading ? '#1e3c72' : '#747D8C'}>
                            {uploading ? uploadPhase : `${totalFileCount} file(s) ready`}
                        </Typography>
                    </Box>
                    <Button onClick={() => setOpenUpload(false)} disabled={uploading} sx={{ color: '#747D8C', fontWeight: 800, textTransform: 'none', borderRadius: '50px', px: 3 }}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={totalFileCount === 0 || !countryId || !divisionId || uploading}
                        onClick={handleUpload}
                        sx={{ borderRadius: '50px', fontWeight: 800, textTransform: 'none', background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: '#FFFFFF', px: 4, py: 1.2, boxShadow: '0 8px 20px rgba(30, 60, 114, 0.3)', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 25px rgba(30, 60, 114, 0.4)' } }}
                    >
                        {uploading ? 'Processing...' : 'Launch Tender 🚀'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rename Dialog */}
            <Dialog open={renameDialogOpen} onClose={closeRenameDialog} maxWidth="xs" fullWidth>
                <Box sx={{ background: 'linear-gradient(135deg, #FFA502 0%, #ECCC68 100%)', p: 3, borderRadius: '24px 24px 0 0', color: 'white' }}>
                    <Typography variant="h5" fontWeight="800">Rename Session</Typography>
                </Box>
                <DialogContent sx={{ p: 4, pt: 5 }}>
                    <TextField autoFocus fullWidth value={renameName} onChange={(e) => setRenameName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !renaming) handleRename(); }} disabled={renaming} sx={friendlyInputStyle} />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={closeRenameDialog} disabled={renaming} sx={{ color: '#747D8C', fontWeight: 800, borderRadius: '50px' }}>Cancel</Button>
                    <Button variant="contained" disabled={!renameName.trim() || renaming} onClick={handleRename} sx={{ borderRadius: '50px', fontWeight: 800, bgcolor: '#FFA502', '&:hover': { bgcolor: '#E08E00' }, px: 4 }}>
                        {renaming ? 'Saving...' : 'Save!'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
                <Box sx={{ background: 'linear-gradient(135deg, #FF4757 0%, #FF6B81 100%)', p: 3, borderRadius: '24px 24px 0 0', color: 'white' }}>
                    <Typography variant="h5" fontWeight="800">Are you sure?</Typography>
                </Box>
                <DialogContent sx={{ p: 4, pt: 3 }}>
                    <Typography variant="body1" color="#2F3542" fontWeight="600" sx={{ lineHeight: 1.6 }}>
                        You are about to delete <strong style={{color: '#FF4757'}}>{deleteTarget?.name || 'Untitled'}</strong>. This will destroy all files and chat history forever! 💥
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={closeDeleteDialog} disabled={deleting} sx={{ color: '#747D8C', fontWeight: 800, borderRadius: '50px' }}>Keep it</Button>
                    <Button variant="contained" disabled={deleting} onClick={handleDelete} sx={{ borderRadius: '50px', fontWeight: 800, bgcolor: '#FF4757', '&:hover': { bgcolor: '#E84118' }, px: 4, boxShadow: '0 8px 20px rgba(255, 71, 87, 0.3)' }}>
                        {deleting ? 'Deleting...' : 'Yes, Delete it!'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Tendering;